import ApiConnector from "../connector/ApiConnector";
import { useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import RootConfig from "../component/RootConfig";
import Note from "../component/Note";
import { useHistory, useLocation } from "react-router-dom";
import LocationService from "../service/RouteUtilService";
import RouteUtilService from "../service/RouteUtilService";
import ConfigService from "../service/ConfigService";
import PosConfig from "../component/configPage/PosConfig";
import EwPayConfig from "../component/configPage/EwPayConfig";
import { faFloppyDisk } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons/faAngleLeft";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";
import { faAnglesDown } from "@fortawesome/free-solid-svg-icons/faAnglesDown";
import { faAnglesUp } from "@fortawesome/free-solid-svg-icons/faAnglesUp";

import { useTableModel } from "../service/TableModelUtilService";
import {
  normalizeRowsForSignature,
  makeSig,
} from "../service/NormalizeForSignatureUtilService";

const Component = () => {
  const [latestConfig, updateLatestConfig] = useImmer(null);
  const [data, updateData] = useImmer(null);
  const [generalConfigData, updateGeneralConfigData] = useImmer(null);
  const [expandAll, setExpandAll] = useState(() => {
    const saved = sessionStorage.getItem('posConfigExpandAll');
    return saved ? JSON.parse(saved) : false;
  });
  const [posConfigOpen, setPosConfigOpen] = useState(() => {
    const saved = sessionStorage.getItem('posConfigOpen');
    return saved ? JSON.parse(saved) : false;
  });
  const [ewPayExpandAll, setEwPayExpandAll] = useState(() => {
    const saved = sessionStorage.getItem('ewPayConfigExpandAll');
    return saved ? JSON.parse(saved) : false;
  });
  const [ewPayConfigOpen, setEwPayConfigOpen] = useState(() => {
    const saved = sessionStorage.getItem('ewPayConfigOpen');
    return saved ? JSON.parse(saved) : false;
  });
  const manualCheckboxChangeRef = useRef(false);
  const ewPayManualCheckboxChangeRef = useRef(false);
  const history = useHistory();
  const location = useLocation();

  const { canSave, setRows: setModelRows, setBaselineFromRows } = useTableModel({
    initialRows: [],
    columns: [],
    keyField: "_key",
    initialSortBy: null,
    initialSortDir: "asc",
    normalizeForSignature: normalizeRowsForSignature,
  });

  const lastRowsSigRef = useRef("");

  const syncToModelIfChanged = (obj) => {
    if (!obj) return;
    const sig = makeSig(obj);
    if (sig !== lastRowsSigRef.current) {
      setModelRows([obj]);
      lastRowsSigRef.current = sig;
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const id = LocationService.getParam(location, "id");

      const isPageInitialized = latestConfig;
      if (!id || !isPageInitialized) {
        const config = await ApiConnector.getLatestConfig();
        if (!mounted) return;
        updateLatestConfig(config);
        updateData(config);
        setModelRows([config]);
        setBaselineFromRows([config]);
        lastRowsSigRef.current = makeSig(config);

        const generalConfig = await ApiConnector.getLatestGeneralConfig();
        if (!mounted) return;
        updateGeneralConfigData(generalConfig);
      }

      if (id) {
        const config = await ApiConnector.getConfig(id);
        if (!mounted) return;
        updateData(config);
        syncToModelIfChanged(config);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [location]);


  useEffect(() => {
    if (data) syncToModelIfChanged(data);
  }, [data]);

  useEffect(() => {
    sessionStorage.setItem('posConfigExpandAll', JSON.stringify(expandAll));
  }, [expandAll]);

  useEffect(() => {
    sessionStorage.setItem('posConfigOpen', JSON.stringify(posConfigOpen));
  }, [posConfigOpen]);

  useEffect(() => {
    sessionStorage.setItem('ewPayConfigExpandAll', JSON.stringify(ewPayExpandAll));
  }, [ewPayExpandAll]);

  useEffect(() => {
    sessionStorage.setItem('ewPayConfigOpen', JSON.stringify(ewPayConfigOpen));
  }, [ewPayConfigOpen]);

  const loadPreviousConfig = () => {
    let id = Number(LocationService.getParam(location, "id"));
    id = id ? id : latestConfig.id;
    const newLocation = LocationService.setParam(location, "id", id - 1);
    history.push(newLocation);
  };

  const loadNextConfig = () => {
    let id = Number(LocationService.getParam(location, "id"));
    if (!id) {
      console.warn("Can't load next config because current config is the latest one.");
      return;
    }
    const newLocation = LocationService.setParam(location, "id", id + 1);
    history.push(newLocation);
  };

  const createConfig = async () => {
    if (!canSave || !data) return;

    const newConfig = await ConfigService.createConfig(data, latestConfig.id);
    if (!newConfig) return;

    updateLatestConfig(newConfig);
    updateData(newConfig);
    setModelRows([newConfig]);
    setBaselineFromRows([newConfig]);
    lastRowsSigRef.current = makeSig(newConfig);

    history.push(RouteUtilService.deleteParam(location, "id"));
  };

  const isLastConfig = () => latestConfig?.id === data?.id;

  return (
    <>
      <header className="subheader header-with-save">
        <h1 className="header-title">Rules</h1>
        <span className="header-save">
          <FontAwesomeIcon
            icon={faFloppyDisk}
            size="2xl"
            className="icon-clickable"
            onClick={canSave ? createConfig : undefined}
            title={canSave ? "Save changes" : "No changes to save"}
            style={{
              opacity: canSave ? 1 : 0.4,
              cursor: canSave ? "pointer" : "not-allowed",
              pointerEvents: canSave ? "auto" : "none",
            }}
            aria-disabled={!canSave}
          />
        </span>
      </header>

      <main>
        {data && generalConfigData && (
          <>
            <div className="row">
              <div className="col">
                <h2>Rules config</h2>
              </div>
              <div className="col-4">
                <h2 className="float-right">
                  <FontAwesomeIcon
                    icon={faAngleLeft}
                    className="icon-clickable"
                    onClick={loadPreviousConfig}
                  />
                  &nbsp;&nbsp;
                  <FontAwesomeIcon
                    icon={faAngleRight}
                    className={`icon-clickable ${isLastConfig() ? "gray" : ""}`}
                    onClick={loadNextConfig}
                  />
                  &nbsp;&nbsp;&nbsp;
                  <Note noteTargetPath="rulesConfig" noteTargetId={data.id} />
                </h2>
              </div>
            </div>

            <RootConfig
              config={data}
              onIdSelected={(selectedId) => {
                const newLocation = LocationService.setParam(location, "id", selectedId);
                history.push(newLocation);
              }}
            ></RootConfig>

            <details open={ewPayConfigOpen} onToggle={(e) => {
              if (e.target === e.currentTarget) {
                const isOpen = e.target.open;
                setEwPayConfigOpen(isOpen);
                sessionStorage.setItem('ewPayConfigOpen', JSON.stringify(isOpen));
              }
            }}>
              <summary>
                <h2>
                  Ew Pay rules config
                  <span className="float-right" style={{ marginRight: '7px' }}>
                    <label style={{ cursor: 'pointer', fontSize: '1.1rem', fontWeight: 'normal', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                      <FontAwesomeIcon icon={ewPayExpandAll ? faAnglesUp : faAnglesDown} />
                      <input
                        type="checkbox"
                        checked={ewPayExpandAll}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          ewPayManualCheckboxChangeRef.current = true;
                          setEwPayExpandAll(checked);
                          setEwPayConfigOpen(checked);
                        }}
                        style={{ transform: 'scale(1.3)', cursor: 'pointer' }}
                      />
                    </label>
                  </span>
                </h2>
              </summary>
              <EwPayConfig
                config={data}
                generalConfig={generalConfigData}
                onConfigChange={(e) => updateData(e)}
                expandAll={ewPayExpandAll && ewPayConfigOpen}
                onExpandAllChange={(value) => {
                  if (!ewPayManualCheckboxChangeRef.current) {
                    setEwPayExpandAll(value);
                  }
                  ewPayManualCheckboxChangeRef.current = false;
                }}
              />
            </details>

            <details open={posConfigOpen} onToggle={(e) => {
              if (e.target === e.currentTarget) {
                const isOpen = e.target.open;
                setPosConfigOpen(isOpen);
                sessionStorage.setItem('posConfigOpen', JSON.stringify(isOpen));
              }
            }}>
              <summary>
                <h2>
                  Point of Sale rules config
                  <span className="float-right" style={{ marginRight: '7px' }}>
                    <label style={{ cursor: 'pointer', fontSize: '1.1rem', fontWeight: 'normal', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                      <FontAwesomeIcon icon={expandAll ? faAnglesUp : faAnglesDown} />
                      <input
                        type="checkbox"
                        checked={expandAll}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          manualCheckboxChangeRef.current = true;
                          setExpandAll(checked);
                          setPosConfigOpen(checked);
                        }}
                        style={{ transform: 'scale(1.3)', cursor: 'pointer' }}
                      />
                    </label>
                  </span>
                </h2>
              </summary>
              <PosConfig
                config={data}
                generalConfig={generalConfigData}
                onConfigChange={(e) => updateData(e)}
                expandAll={expandAll && posConfigOpen}
                onExpandAllChange={(value) => {
                  if (!manualCheckboxChangeRef.current) {
                    setExpandAll(value);
                  }
                  manualCheckboxChangeRef.current = false;
                }}
              />
            </details>
          </>
        )}
      </main>
    </>
  );
};

export default Component;
